import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface SchemaProperty {
  type: string | string[];
  title?: string;
  description?: string;
  default?: any;
  enum?: any[];
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  editor?: string;
  prefill?: string;
}

interface ActorConfigFormProps {
  schema: any;
  initialValues: Record<string, any>;
  onSave: (values: Record<string, any>) => Promise<void>;
  isSaving: boolean;
}

export function ActorConfigForm({ schema, initialValues, onSave, isSaving }: ActorConfigFormProps) {
  const [values, setValues] = useState<Record<string, any>>(initialValues || {});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setValues(initialValues || {});
    setHasChanges(false);
  }, [initialValues]);

  const handleChange = (key: string, value: any) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(values);
    setHasChanges(false);
  };

  const handleReset = () => {
    setValues(initialValues || {});
    setHasChanges(false);
  };

  if (!schema || !schema.properties) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p className="text-sm">No configuration schema available for this actor</p>
      </div>
    );
  }

  const renderField = (key: string, property: SchemaProperty) => {
    const value = values[key] !== undefined ? values[key] : property.default;
    const types = Array.isArray(property.type) ? property.type : [property.type];
    const mainType = types[0];

    const label = property.title || key;
    const description = property.description;

    if (property.enum && property.enum.length > 0) {
      return (
        <div key={key} className="space-y-2">
          <Label htmlFor={key} className="text-sm font-medium">
            {label}
          </Label>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
          <Select value={String(value || "")} onValueChange={(val) => handleChange(key, val)}>
            <SelectTrigger id={key} className="h-9">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {property.enum.map((option) => (
                <SelectItem key={String(option)} value={String(option)}>
                  {String(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    switch (mainType) {
      case "boolean":
        return (
          <div key={key} className="flex items-center justify-between space-x-2 py-2">
            <div className="space-y-0.5">
              <Label htmlFor={key} className="text-sm font-medium">
                {label}
              </Label>
              {description && <p className="text-xs text-muted-foreground">{description}</p>}
            </div>
            <Switch
              id={key}
              checked={value === true}
              onCheckedChange={(checked) => handleChange(key, checked)}
            />
          </div>
        );

      case "number":
      case "integer":
        return (
          <div key={key} className="space-y-2">
            <Label htmlFor={key} className="text-sm font-medium">
              {label}
            </Label>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
            <Input
              id={key}
              type="number"
              value={value || ""}
              onChange={(e) => handleChange(key, e.target.value ? Number(e.target.value) : undefined)}
              min={property.minimum}
              max={property.maximum}
              className="h-9"
            />
          </div>
        );

      case "array":
        const arrayValue = Array.isArray(value) ? value.join(", ") : "";
        return (
          <div key={key} className="space-y-2">
            <Label htmlFor={key} className="text-sm font-medium">
              {label}
            </Label>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
            <Input
              id={key}
              value={arrayValue}
              onChange={(e) => handleChange(key, e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              placeholder="Comma-separated values"
              className="h-9"
            />
          </div>
        );

      case "object":
        const objectValue = typeof value === "object" ? JSON.stringify(value, null, 2) : "";
        return (
          <div key={key} className="space-y-2">
            <Label htmlFor={key} className="text-sm font-medium">
              {label}
            </Label>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
            <Textarea
              id={key}
              value={objectValue}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handleChange(key, parsed);
                } catch {
                  // Invalid JSON, keep as string temporarily
                }
              }}
              placeholder="{}"
              className="font-mono text-xs min-h-[100px]"
            />
          </div>
        );

      case "string":
      default:
        const isLongText = property.editor === "textarea" || (property.maxLength && property.maxLength > 200);

        if (isLongText) {
          return (
            <div key={key} className="space-y-2">
              <Label htmlFor={key} className="text-sm font-medium">
                {label}
              </Label>
              {description && <p className="text-xs text-muted-foreground">{description}</p>}
              <Textarea
                id={key}
                value={value || ""}
                onChange={(e) => handleChange(key, e.target.value)}
                placeholder={property.prefill || ""}
                className="min-h-[100px]"
              />
            </div>
          );
        }

        return (
          <div key={key} className="space-y-2">
            <Label htmlFor={key} className="text-sm font-medium">
              {label}
            </Label>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
            <Input
              id={key}
              value={value || ""}
              onChange={(e) => handleChange(key, e.target.value)}
              placeholder={property.prefill || ""}
              className="h-9"
              maxLength={property.maxLength}
            />
          </div>
        );
    }
  };

  const properties = schema.properties || {};
  const propertyKeys = Object.keys(properties);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {propertyKeys.map((key) => renderField(key, properties[key]))}
      </div>

      {propertyKeys.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">No configurable fields available</p>
        </div>
      )}

      {propertyKeys.length > 0 && (
        <div className="flex items-center gap-2 pt-4 border-t">
          <Button
            type="submit"
            disabled={!hasChanges || isSaving}
            className="flex-1"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Configuration"
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={!hasChanges || isSaving}
          >
            Reset
          </Button>
        </div>
      )}
    </form>
  );
}
